import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import BoltIcon from '@mui/icons-material/Bolt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { AccountSidebarPreview } from './AccountSidebarPreview';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? drawerWidth + 16 : `calc(${theme.spacing(7)} + 1px)`,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  '& .MuiDrawer-paper': {
    borderRadius: 8,
    border: `0px solid ${theme.palette.divider}`,
    margin: 8,
    height: 'calc(100% - 16px) !important',
    backgroundColor: theme.palette.background.paper,
    //boxShadow: 'rgba(200, 200, 200, 0.35) 0px 0px 15px',
    color: theme.palette.text.primary,
    ...(open && {
      ...openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
    }),
  },
  [theme.breakpoints.up('sm')]: {
    width: open ? drawerWidth + 16 : `calc(${theme.spacing(8)} + 16px)`,
  },
}));

const allMenuItemsTop = [
  { text: 'Home', path: '/', icon: <HomeIcon /> },
  { text: 'Clienti', path: '/customers', icon: <BusinessIcon /> },
  { text: 'Diagnosi', path: '/diagnosis', icon: <AssignmentIcon /> },
  { text: 'Ricerca AI', path: '/ai-search', icon: <TravelExploreIcon /> },
];

const allMenuItemsDown = [
  {
    text: 'Elenco opzioni',
    path: '/field-options',
    icon: <SettingsIcon />,
    roles: ['admin', 'mantainer'],
  },
  {
    text: 'Fattori di conversione',
    path: '/energy-vectors',
    icon: <BoltIcon />,
    roles: ['admin', 'mantainer'],
  },
  {
    text: 'Usi energetici',
    path: '/energy-use',
    icon: <ElectricalServicesIcon />,
    roles: ['admin', 'mantainer'],
  },
  {
    text: 'Incidenze ENEA',
    path: '/incidence-enea',
    icon: <AssessmentIcon />,
    roles: ['admin', 'mantainer'],
  },
  { text: 'Utenti', path: '/users', icon: <PersonIcon />, roles: ['admin', 'mantainer'] },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);

  // Filtra i menu items in base al ruolo dell'utente
  const menuItemsUp = allMenuItemsTop;

  const menuItemsDown = allMenuItemsDown.filter(item => {
    if (!item.roles) return true; // Se non ha restrizioni di ruolo, mostra sempre
    if (!user?.role) return false; // Se l'utente non ha ruolo, non mostra
    return item.roles.includes(user.role);
  });

  const handleToggle = () => {
    setOpen(!open);
  };

  const isActive = (path: string) =>
    path === '/' ? location.pathname === path : location.pathname.startsWith(path);

  const buildList = (menuItems: any[]) => {
    return (
      <List>
        {menuItems.map(item => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={isActive(item.path)}
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: open ? 'initial' : 'center',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                  mr: open ? 3 : 'auto',
                  color: isActive(item.path)
                    ? theme.palette.primary.light
                    : theme.palette.text.primary,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  opacity: open ? 1 : 0,
                  color: isActive(item.path)
                    ? theme.palette.primary.light
                    : theme.palette.text.primary,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      <Drawer variant="permanent" open={open}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <DrawerHeader
            sx={{
              display: 'flex',
              justifyContent: 'start',
            }}
          >
            <img
              onClick={handleToggle}
              src={open ? '/logo-full.png' : '/logo-icon.png'}
              alt="Logo"
              height={32}
              style={{
                height: '32px',
                marginRight: open ? '3.4rem' : '0px',
                marginLeft: '0.5rem',
                objectFit: 'cover',
                objectPosition: 'left center',
                transition: theme.transitions.create(['width', 'margin-right'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                cursor: 'pointer',
              }}
            />

            {open && (
              <IconButton onClick={handleToggle} sx={{ color: 'text.primary' }}>
                {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            )}
          </DrawerHeader>
          <Divider sx={{ borderColor: 'divider' }} />
          {buildList(menuItemsUp)}
          <Box sx={{ flexGrow: 1, cursor: 'col-resize' }} onClick={handleToggle}></Box>
          {buildList(menuItemsDown)}
          <Box onClick={e => e.stopPropagation()}>
            <AccountSidebarPreview mini={!open} open={open} />
          </Box>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pr: 1,
          pt: 1,
          pb: 1,
          bgcolor: 'background.default',
          height: '100vh',
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          width: open ? `calc(100% - ${drawerWidth + 16}px)` : `calc(100% - ${theme.spacing(11)})`,
          [theme.breakpoints.up('sm')]: {
            width: open
              ? `calc(100% - ${drawerWidth + 16}px)`
              : `calc(100% - ${theme.spacing(10)})`,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
